/**
 * Unified Service Request Types
 *
 * This file contains the unified service request types that work for all entity types
 * (residents, vendors, managers) while maintaining entity-specific access patterns.
 */

import { UserRole } from './auth';

// ====== CORE SERVICE REQUEST TYPES ======

export interface ServiceRequest {
  id: string;
  title: string;
  description: string;
  category: ServiceRequestCategory;
  priority: ServiceRequestPriority;
  status: ServiceRequestStatus;

  // Requester information
  requestedBy: {
    id: string;
    name: string;
    role: UserRole;
    unitNumber?: string;
    building?: string;
    contactInfo?: {
      email?: string;
      phone?: string;
      preferredMethod?: 'email' | 'phone' | 'app' | 'text';
    };
  };

  // Location details
  location: {
    building?: string;
    unit?: string;
    room?: string;
    commonArea?: string;
    specificLocation?: string;
    accessInstructions?: string;
  };

  // Budget information
  budget?: {
    min?: number;
    max?: number;
    currency: string;
    paymentMethod?: 'cash' | 'check' | 'card' | 'community_account';
    approvalRequired?: boolean;
  };

  // Timeline and scheduling
  timeline?: {
    preferredStartDate?: string;
    deadline?: string;
    estimatedDuration?: string;
    flexibleScheduling?: boolean;
    timeRestrictions?: {
      availableDays?: string[];
      availableHours?: {
        start: string;
        end: string;
      };
      excludedDates?: string[];
    };
  };

  // Requirements and preferences
  requirements?: {
    preferredVendors?: string[];
    excludedVendors?: string[];
    certifications?: string[];
    insuranceRequired?: boolean;
    bonded?: boolean;
    backgroundCheck?: boolean;
    minimumRating?: number;
    accessRequirements?: {
      keyAccess?: boolean;
      escortRequired?: boolean;
      petsPresent?: boolean;
      specialInstructions?: string;
    };
  };

  // File attachments
  attachments: ServiceRequestAttachment[];

  // Vendor responses/bids
  responses?: ServiceRequestResponse[];

  // Assignment information
  assignment?: {
    assignedVendorId?: string;
    assignedVendorName?: string;
    assignedAt?: string;
    assignedBy?: string;
    estimatedCost?: number;
    estimatedDuration?: string;
    scheduledDate?: string;
    workOrderId?: string;
  };

  // Approval workflow
  approval?: {
    required: boolean;
    approvedBy?: string;
    approvedAt?: string;
    approvalNotes?: string;
    rejectedReason?: string;
  };

  // Completion and feedback
  completion?: {
    completedAt?: string;
    actualCost?: number;
    actualDuration?: string;
    workQuality?: number; // 1-5
    feedback?: {
      rating: number;
      comment?: string;
      wouldRecommend?: boolean;
      issues?: string[];
    };
  };

  // Metadata
  createdAt: string;
  updatedAt: string;
  lastActivity?: string;
}

// ====== SERVICE REQUEST RESPONSE/BID ======

export interface ServiceRequestResponse {
  id: string;
  serviceRequestId: string;
  vendorId: string;
  vendorName: string;
  vendorRating?: number;
  vendorAvatar?: string;
  vendorPhone?: string;
  vendorEmail?: string;

  // Proposal details
  message: string;
  estimatedCost?: number;
  estimatedDuration?: string;
  availability?: string;
  warranty?: string;
  experience?: string;

  // Vendor credentials
  certifications?: string[];
  portfolio?: ServiceRequestPortfolioItem[];
  reviewCount?: number;

  // Response status
  status: 'pending' | 'accepted' | 'rejected' | 'withdrawn';

  // Timestamps
  createdAt: string;
  updatedAt: string;
}

export interface ServiceRequestPortfolioItem {
  id: string;
  title: string;
  description: string;
  imageUrl?: string;
  completedAt: string;
}

export interface ServiceRequestAttachment {
  id: string;
  name: string;
  url: string;
  type: 'photo' | 'document' | 'video';
  description?: string;
  size: number;
  uploadedAt: string;
}

// ====== ENUMS AND CONSTANTS ======

export type ServiceRequestCategory =
  | 'hvac'
  | 'plumbing'
  | 'electrical'
  | 'landscaping'
  | 'cleaning'
  | 'security'
  | 'painting'
  | 'carpentry'
  | 'general_maintenance'
  | 'pest_control'
  | 'appliance_repair'
  | 'flooring'
  | 'roofing'
  | 'emergency'
  | 'other';

export type ServiceRequestPriority = 'low' | 'medium' | 'high' | 'urgent';

export type ServiceRequestStatus =
  | 'draft' // Being created by resident
  | 'submitted' // Submitted and open for bids
  | 'open' // Alternative name for submitted (vendor perspective)
  | 'in_review' // Being reviewed by management
  | 'approved' // Approved and ready for assignment
  | 'assigned' // Vendor assigned
  | 'in_progress' // Work in progress
  | 'completed' // Work completed
  | 'cancelled' // Cancelled by resident
  | 'rejected'; // Rejected by management

// ====== FILTERS AND SEARCH ======

export interface ServiceRequestFilters {
  category?: ServiceRequestCategory[];
  priority?: ServiceRequestPriority[];
  status?: ServiceRequestStatus[];
  dateRange?: {
    start: string;
    end: string;
  };
  budgetRange?: {
    min: number;
    max: number;
  };
  location?: {
    building?: string;
    unit?: string;
  };
  assignedVendor?: string;
  requestedBy?: string;
}

// ====== ACCESS CONTROL ======

export interface ServiceRequestEntityAccess {
  canView: (userRole: UserRole, serviceRequest: ServiceRequest, userId?: string) => boolean;
  canEdit: (userRole: UserRole, serviceRequest: ServiceRequest, userId?: string) => boolean;
  canRespond: (userRole: UserRole, serviceRequest: ServiceRequest, userId?: string) => boolean;
  canAssign: (userRole: UserRole, serviceRequest: ServiceRequest, userId?: string) => boolean;
  canApprove: (userRole: UserRole, serviceRequest: ServiceRequest, userId?: string) => boolean;
  canCancel: (userRole: UserRole, serviceRequest: ServiceRequest, userId?: string) => boolean;
}

export const serviceRequestEntityAccess: ServiceRequestEntityAccess = {
  canView: (userRole, serviceRequest, userId) => {
    switch (userRole) {
      case 'super_admin':
      case 'admin':
        return true;
      case 'moderator':
        return true;
      case 'vendor':
        return (
          ['submitted', 'open', 'approved'].includes(serviceRequest.status) ||
          serviceRequest.responses?.some((r) => r.vendorId === userId) ||
          serviceRequest.assignment?.assignedVendorId === userId
        );
      case 'resident':
        return serviceRequest.requestedBy.id === userId;
      default:
        return false;
    }
  },

  canEdit: (userRole, serviceRequest, userId) => {
    switch (userRole) {
      case 'super_admin':
      case 'admin':
        return true;
      case 'moderator':
        return ['submitted', 'open', 'in_review'].includes(serviceRequest.status);
      case 'resident':
        return (
          serviceRequest.requestedBy.id === userId &&
          ['draft', 'submitted', 'open'].includes(serviceRequest.status)
        );
      default:
        return false;
    }
  },

  canRespond: (userRole, serviceRequest, userId) => {
    return (
      userRole === 'vendor' &&
      ['submitted', 'open', 'approved'].includes(serviceRequest.status) &&
      !serviceRequest.responses?.some((r) => r.vendorId === userId)
    );
  },

  canAssign: (userRole, serviceRequest, userId) => {
    switch (userRole) {
      case 'super_admin':
      case 'admin':
        return ['submitted', 'open', 'approved', 'in_review'].includes(serviceRequest.status);
      case 'moderator':
        return ['submitted', 'open', 'approved'].includes(serviceRequest.status);
      case 'resident':
        return (
          serviceRequest.requestedBy.id === userId &&
          ['submitted', 'open', 'approved'].includes(serviceRequest.status) &&
          serviceRequest.responses &&
          serviceRequest.responses.length > 0
        );
      default:
        return false;
    }
  },

  canApprove: (userRole, serviceRequest, userId) => {
    return (
      ['super_admin', 'admin', 'moderator'].includes(userRole) &&
      ['submitted', 'in_review'].includes(serviceRequest.status)
    );
  },

  canCancel: (userRole, serviceRequest, userId) => {
    switch (userRole) {
      case 'super_admin':
      case 'admin':
        return !['completed', 'cancelled'].includes(serviceRequest.status);
      case 'moderator':
        return ['submitted', 'open', 'in_review'].includes(serviceRequest.status);
      case 'resident':
        return (
          serviceRequest.requestedBy.id === userId &&
          !['completed', 'cancelled'].includes(serviceRequest.status)
        );
      default:
        return false;
    }
  },
};

// ====== LABELS AND DISPLAY ======

export const categoryLabels: Record<ServiceRequestCategory, string> = {
  hvac: 'HVAC & Climate Control',
  plumbing: 'Plumbing Services',
  electrical: 'Electrical Work',
  landscaping: 'Landscaping & Grounds',
  cleaning: 'Cleaning Services',
  security: 'Security & Access',
  painting: 'Painting & Decorating',
  carpentry: 'Carpentry & Woodwork',
  general_maintenance: 'General Maintenance',
  pest_control: 'Pest Control',
  appliance_repair: 'Appliance Repair',
  flooring: 'Flooring Services',
  roofing: 'Roofing Services',
  emergency: 'Emergency Services',
  other: 'Other Services',
};

export const priorityLabels: Record<ServiceRequestPriority, string> = {
  low: 'Low Priority',
  medium: 'Medium Priority',
  high: 'High Priority',
  urgent: 'Urgent',
};

export const statusLabels: Record<ServiceRequestStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  open: 'Open for Bids',
  in_review: 'Under Review',
  approved: 'Approved',
  assigned: 'Assigned',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  rejected: 'Rejected',
};

// ====== UTILITY TYPES ======

export interface ServiceRequestSummary {
  total: number;
  byStatus: Record<ServiceRequestStatus, number>;
  byCategory: Record<ServiceRequestCategory, number>;
  byPriority: Record<ServiceRequestPriority, number>;
  avgResponseTime?: number; // hours
  avgCompletionTime?: number; // hours
  avgSatisfactionRating?: number;
  totalSpent?: number;
}

export interface ServiceRequestHistory {
  id: string;
  serviceRequestId: string;
  action:
    | 'created'
    | 'updated'
    | 'submitted'
    | 'approved'
    | 'assigned'
    | 'started'
    | 'completed'
    | 'cancelled'
    | 'message_sent';
  timestamp: string;
  details?: string;
  performedBy: {
    id: string;
    name: string;
    role: UserRole;
  };
  systemGenerated: boolean;
}
